import React from "react";
import { useTranslation } from "react-i18next";

import { Button, Stack, Grid, Typography, Box, Container, Divider, Link } from "@mui/material";
import DashboardLayout from "../../Layouts/Dashboard/dashboard";
import Slider, { Slide } from "../Slider";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { routePaths } from "../../Router/router";
import { Link as RouterLink, useNavigate } from "react-router";
import AutocompleteSearch from "../../Components/Search/AutocompleteSearch/autocomplete-search";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import { cacheService, searchService } from "../../Core/Api/services";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { StyledTitle } from "../../Pages/Catalog/CatalogIndex/catalog-index-home";
import AssetCard, { AssetCardProps } from "../Cards/AssetCard/asset-card";
import AssetCardSkeleton from "../Cards/AssetCard/asset-card-skeleton";
import HomeHeroBackground from "../Heros/HomeHeroBackground";
import { SearchRequestNative } from "../../Core/Api/Types/search-request-native";
import { ComputeMetadataBaseType } from "../../Core/Utils/functions";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";

function AdamIndex() {
  return (
    <DashboardLayout showTopbarSearchOnScroll>
      <AdamHome />
    </DashboardLayout>
  );
}

const AdamHome: React.FC = () => {
  const { t } = useTranslation();
  const maxItems = 10;

  const adamTypeMeta = cacheService.GetMetadata().find((x) => x.tagName === "adamAssetType");

  const [isLoadingSources, setIsLoadingSources] = React.useState<boolean>(true);
  const [isLoadingDelivery, setIsLoadingDelivery] = React.useState<boolean>(true);

  const user = cacheService.GetUserInfo();

  const [latestSources, setLatestSources] = React.useState<Omit<AssetCardProps, "key">[]>([]);
  const [latestDelivery, setLatestDelivery] = React.useState<Omit<AssetCardProps, "key">[]>([]);

  const navigate = useNavigate();

  const loadLatestSources = () => {
    if (!adamTypeMeta) return;

    searchService
      .SearchBasic(
        new SearchRequestNative({
          hideTitleWithoutAsset: false,
          outputLayer: ObjectLayer.Asset,
          pattern: "*",
          limit: maxItems,
          metadata: [
            {
              $type: ComputeMetadataBaseType(BaseMetadataType.DbStringMetadataValueInfos),
              id: adamTypeMeta.id,
              fieldType: BaseMetadataType.DbStringMetadataValueInfos,
              value: "source",
              creationTime: new Date().toISOString(),
              updateTime: null,
              fieldName: null,
              fieldLabel: null,
              targetLayer: ObjectLayer.Asset,
              valueLabel: "Source",
            },
          ],
        })
      )
      .then((response) => {
        if (response.results) {
          const sourcesCardsData: Omit<AssetCardProps, "key">[] = response.results!.reverse().map((x) => ({
            obj: x,
            labelList: [x.type],
          }));

          setLatestSources(sourcesCardsData);
        }
        setIsLoadingSources(false);
      });
  };

  const loadLatestDelivery = () => {
    if (!adamTypeMeta) return;
    searchService
      .SearchBasic(
        new SearchRequestNative({
          hideTitleWithoutAsset: false,
          outputLayer: ObjectLayer.Asset,
          pattern: "*",
          limit: maxItems,
          metadata: [
            {
              $type: ComputeMetadataBaseType(BaseMetadataType.DbStringMetadataValueInfos),
              id: adamTypeMeta.id,
              fieldType: BaseMetadataType.DbStringMetadataValueInfos,
              value: "trailer",
              creationTime: new Date().toISOString(),
              updateTime: null,
              fieldName: null,
              fieldLabel: null,
              targetLayer: ObjectLayer.Asset,
              valueLabel: "Trailer",
            },
          ],
        })
      )
      .then((response) => {
        if (response.results) {
          const deliveryCardsData: Omit<AssetCardProps, "key">[] = response.results!.reverse().map((x) => ({
            obj: x,
            labelList: [x.type],
          }));

          setLatestDelivery(deliveryCardsData);
        }
        setIsLoadingDelivery(false);
      });
  };

  React.useEffect(() => {
    loadLatestSources();
    loadLatestDelivery();
  }, []);

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <HomeHeroBackground covers={[]} visibleCoverIndex={0} />
      </Box>
      <Container sx={{ pt: 12, mt: 8, mb: 6, position: "relative" }}>
        <StyledTitle variant="h1" fontWeight={700} textAlign="center">
          {cacheService.GetCatchPhrase()}
        </StyledTitle>
        <Grid container justifyContent="center" sx={{ mb: 6, mt: 1 }}>
          <Grid item md={6}>
            <AutocompleteSearch />
            <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mt: 3 }}>
              <Button
                component={RouterLink}
                to={`${routePaths.inventorySourceAdam.path}`}
                variant="rounded"
                size="xlarge"
              >
                {t("route:linkTitle.sources")}
              </Button>
              <Divider orientation="vertical" variant="middle" flexItem sx={{ height: 12, m: "auto 0" }} />
              <Button component={RouterLink} to={`${routePaths.inventoryAssets.path}`} variant="rounded" size="xlarge">
                {t("common:commandes")}
              </Button>
              <Button
                component={RouterLink}
                to={`${routePaths.ordersItemDetails.path}`}
                variant="rounded"
                size="xlarge"
              >
                Suivi livraisons
              </Button>
              {user?.customerIds && user?.customerIds.length > 0 && (
                <>
                  <Divider orientation="vertical" variant="middle" flexItem sx={{ height: 12, m: "auto 0" }} />

                  <Button
                    component={RouterLink}
                    to={`${routePaths.catalog.path}/find/*`}
                    variant="rounded"
                    size="xlarge"
                    startIcon={<GridViewOutlinedIcon />}
                  >
                    {t("button:browseAll")}
                  </Button>
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
        <Box component="section" sx={{ mt: 4, mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography component="h2" variant="h3">
              {t("common:latestSources")}
            </Typography>
            <Link
              component={RouterLink}
              to={routePaths.inventorySourceAdam.path}
              variant="body1"
              color="white"
              underline="hover"
            >
              {t("common:seeAll")}
              <KeyboardArrowRightIcon fontSize="small" />
            </Link>
          </Stack>
          {isLoadingSources ? (
            <Grid container spacing={2} sx={{ pb: 3 }}>
              {Array(5)
                .fill("loading")
                .map((a, i) => (
                  <Grid item md={2.4} key={i}>
                    <AssetCardSkeleton />
                  </Grid>
                ))}
            </Grid>
          ) : (
            latestSources.length > 0 && (
              <Slider
                sx={{
                  pb: 3,
                  ".swiper-slide": {
                    flexShrink: 1,
                    height: "auto",
                  },
                  ".swiper-wrapper": {
                    mb: 1,
                  },
                  ".MuiCard-root": {
                    width: "265px",
                  },
                }}
                paginate={true}
                background={"default"}
                slidesPerGroup={6}
                spaceBetween={16}
              >
                {latestSources.map((src, index) => (
                  <Slide key={src.obj.id}>
                    <AssetCard
                      selectMode={false}
                      {...src}
                      onCardClick={() => navigate(`${routePaths.asset.path}/${src.obj.id}`)}
                    />
                  </Slide>
                ))}
              </Slider>
            )
          )}
        </Box>
        <Box component="section" sx={{ mt: 4, mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography component="h2" variant="h3">
              {t("common:latestTrailers")}
            </Typography>
            <Link
              component={RouterLink}
              to={routePaths.inventoryAssets.path}
              variant="body1"
              color="white"
              underline="hover"
            >
              {t("common:seeAll")}
              <KeyboardArrowRightIcon fontSize="small" />
            </Link>
          </Stack>
          {isLoadingDelivery ? (
            <Grid container spacing={2} sx={{ pb: 3 }}>
              {Array(5)
                .fill("loading")
                .map((a, i) => (
                  <Grid item md={2.4} key={i}>
                    <AssetCardSkeleton />
                  </Grid>
                ))}
            </Grid>
          ) : (
            latestDelivery.length > 0 && (
              <Slider
                sx={{
                  pb: 3,
                  ".swiper-slide": {
                    flexShrink: 1,
                    height: "auto",
                  },
                  ".swiper-wrapper": {
                    mb: 1,
                  },
                  ".MuiCard-root": {
                    width: "265px",
                  },
                }}
                paginate={true}
                background={"default"}
                slidesPerGroup={6}
                spaceBetween={16}
              >
                {latestDelivery.map((del, index) => (
                  <Slide key={del.obj.id}>
                    <AssetCard
                      selectMode={false}
                      onCardClick={() => navigate(`${routePaths.asset.path}/${del.obj.id}`)}
                      {...del}
                    />
                  </Slide>
                ))}
              </Slider>
            )
          )}
        </Box>
      </Container>
    </>
  );
};

export default AdamIndex;
